// ============================================================
// src/hooks/useCalendar.ts
// Changements vs version originale :
// ============================================================

import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
  getDocs,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Meeting, MeetingFormData, MeetingStatus, CalendarEvent } from '@/types/calendar.types';
import { useToast } from '@/hooks/use-toast';
import { generateJitsiMeetingUrl } from '@/utils/jitsi';
import { invokeFunctionSilent } from '@/lib/cloudrun';

export function useCallCenter(dealId?: string) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);

  // ── Écoute temps réel des réunions ──────────────────────────

  useEffect(() => {
    if (!user?.id) { setLoading(false); return; }

    const q = dealId
      ? query(collection(db, 'meetings'), where('dealId', '==', dealId))
      : query(collection(db, 'meetings'), where('participants', 'array-contains', user.id));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt?.toDate() || new Date(),
        updatedAt: d.data().updatedAt?.toDate(),
        confirmedAt: d.data().confirmedAt?.toDate(),
        completedAt: d.data().completedAt?.toDate(),
      })) as Meeting[];

      // Tri côté client — évite l'index composite Firestore
      data.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      setMeetings(data);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching meetings:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user?.id, dealId]);

  // ── Email de réunion via Cloud Run ───────────────────────────

  const sendMeetingEmail = useCallback(async (
    contactEmail: string,
    contactFirstName: string,
    contactRole: string,
    eventType: 'confirmation' | 'cancelled' | 'rescheduled',
    meetingTitle: string,
    scheduledAt: string,
    duration: string,
    meetingUrl?: string,
  ) => {
    await invokeFunctionSilent('send-meeting-email', {
      email: contactEmail,
      firstName: contactFirstName,
      role: contactRole,
      eventType,
      language: 'en',
      meetingTitle,
      scheduledAt,
      duration,
      meetingLink: meetingUrl,
    });
  }, []);

  // ── Vérifier si un email est membre de la plateforme ────────

  const checkIsMember = useCallback(async (email: string): Promise<boolean> => {
    try {
      const snap = await getDocs(
        query(collection(db, 'users'), where('email', '==', email))
      );
      return !snap.empty;
    } catch {
      return false;
    }
  }, []);

  // ── Planifier une réunion ────────────────────────────────────

  const scheduleMeeting = useCallback(async (data: MeetingFormData): Promise<string | null> => {
    if (!user?.id) return null;
    try {
      const participants = [user.id, ...data.participants.filter(p => p !== user.id)];
      const meetingUrl = data.format === 'video'
        ? generateJitsiMeetingUrl(undefined, data.title)
        : undefined;

      const docRef = await addDoc(collection(db, 'meetings'), {
        ...data,
        contacts: data.contacts || (data.contact ? [data.contact] : []),
        organizerId: user.id,
        participants,
        status: 'pending' as MeetingStatus,
        createdAt: Timestamp.now(),
        ...(meetingUrl && { meetingUrl }),
      });

      // Notifications pour les participants
      for (const participantId of participants) {
        if (participantId !== user.id) {
          await addDoc(collection(db, 'userNotifications'), {
            userId: participantId,
            type: 'meeting_scheduled',
            title: 'New Meeting Scheduled',
            message: `You have been invited to: ${data.title}`,
            relatedMeetingId: docRef.id,
            relatedDealId: data.dealId,
            createdAt: Timestamp.now(),
            seen: false,
          });
        }
      }

      // Email au contact externe
      if (data.contact?.email) {
        await sendMeetingEmail(
          data.contact.email,
          data.contact.firstName,
          data.contact.role || 'external',
          'confirmation',
          data.title,
          `${data.date} ${data.time}`,
          `${data.duration} min`,
          meetingUrl,
        );

        const isMember = await checkIsMember(data.contact.email);
        if (isMember) console.log('Contact is a platform member - internal notification sent');
      }

      toast({ title: 'Meeting Scheduled', description: 'Your meeting has been scheduled successfully.' });
      return docRef.id;
    } catch (error) {
      console.error('Error scheduling meeting:', error);
      toast({ title: 'Error', description: 'Failed to schedule meeting.', variant: 'destructive' });
      return null;
    }
  }, [user?.id, toast, sendMeetingEmail, checkIsMember]);

  // ── Mettre à jour une réunion ────────────────────────────────

  const updateMeeting = useCallback(async (meetingId: string, data: Partial<MeetingFormData>): Promise<boolean> => {
    try {
      await updateDoc(doc(db, 'meetings', meetingId), { ...data, updatedAt: Timestamp.now() });
      toast({ title: 'Meeting Updated', description: 'Your meeting has been updated successfully.' });
      return true;
    } catch (error) {
      console.error('Error updating meeting:', error);
      toast({ title: 'Error', description: 'Failed to update meeting.', variant: 'destructive' });
      return false;
    }
  }, [toast]);

  // ── Mettre à jour le statut — génère Jitsi à la confirmation ─

  const updateMeetingStatus = useCallback(async (meetingId: string, status: MeetingStatus): Promise<boolean> => {
    try {
      const updateData: Record<string, unknown> = { status, updatedAt: Timestamp.now() };

      if (status === 'confirmed') {
        updateData.confirmedAt = Timestamp.now();
        const meeting = meetings.find(m => m.id === meetingId);
        if (meeting && !meeting.meetingUrl) {
          updateData.meetingUrl = generateJitsiMeetingUrl(undefined, meeting.title);
        }
      } else if (status === 'completed') {
        updateData.completedAt = Timestamp.now();
      }

      await updateDoc(doc(db, 'meetings', meetingId), updateData);

      const meeting = meetings.find(m => m.id === meetingId);
      if (meeting) {
        // Notifier tous les participants
        for (const participantId of meeting.participants) {
          await addDoc(collection(db, 'userNotifications'), {
            userId: participantId,
            type: `meeting_${status}`,
            title: `Meeting ${status.charAt(0).toUpperCase() + status.slice(1)}`,
            message: `The meeting "${meeting.title}" has been ${status}.${
              status === 'confirmed' && updateData.meetingUrl
                ? ' A video conference link has been generated.'
                : ''
            }`,
            relatedMeetingId: meetingId,
            relatedDealId: meeting.dealId,
            createdAt: Timestamp.now(),
            seen: false,
          });
        }

        // Email au contact si confirmation ou annulation
        if (meeting.contact?.email && (status === 'confirmed' || status === 'cancelled')) {
          await sendMeetingEmail(
            meeting.contact.email,
            meeting.contact.firstName,
            meeting.contact.role || 'external',
            status === 'confirmed' ? 'confirmation' : 'cancelled',
            meeting.title,
            `${meeting.date} ${meeting.time}`,
            `${meeting.duration} min`,
            status === 'confirmed'
              ? (updateData.meetingUrl as string || meeting.meetingUrl)
              : undefined,
          );
        }
      }

      toast({ title: 'Status Updated', description: `Meeting has been ${status}.` });
      return true;
    } catch (error) {
      console.error('Error updating meeting status:', error);
      toast({ title: 'Error', description: 'Failed to update meeting status.', variant: 'destructive' });
      return false;
    }
  }, [meetings, toast, sendMeetingEmail]);

  // ── Supprimer une réunion ────────────────────────────────────

  const deleteMeeting = useCallback(async (meetingId: string): Promise<boolean> => {
    try {
      await deleteDoc(doc(db, 'meetings', meetingId));
      toast({ title: 'Meeting Deleted', description: 'The meeting has been deleted.' });
      return true;
    } catch (error) {
      console.error('Error deleting meeting:', error);
      toast({ title: 'Error', description: 'Failed to delete meeting.', variant: 'destructive' });
      return false;
    }
  }, [toast]);

  // ── Événements pour le calendrier UI ────────────────────────

  const getCalendarEvents = useCallback((): CalendarEvent[] => {
    return meetings.map(meeting => {
      const [hours, minutes] = meeting.time.split(':').map(Number);
      const start = new Date(meeting.date);
      start.setHours(hours, minutes, 0, 0);
      const end = new Date(start);
      end.setMinutes(end.getMinutes() + meeting.duration);
      return { id: meeting.id, title: meeting.title, start, end, type: meeting.type, status: meeting.status, meeting };
    });
  }, [meetings]);

  // ── Filtres ──────────────────────────────────────────────────

  const getUpcomingMeetings = useCallback(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return meetings.filter(m => {
      const d = new Date(m.date + 'T12:00:00'); d.setHours(0, 0, 0, 0);
      return d >= today && m.status !== 'cancelled' && m.status !== 'completed';
    });
  }, [meetings]);

  const getPastMeetings = useCallback(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return meetings.filter(m => {
      const d = new Date(m.date + 'T12:00:00'); d.setHours(0, 0, 0, 0);
      return d < today || m.status === 'completed';
    });
  }, [meetings]);

  return {
    meetings, loading, scheduleMeeting, updateMeeting,
    updateMeetingStatus, deleteMeeting, getCalendarEvents,
    getUpcomingMeetings, getPastMeetings,
    // Placeholder fields until call-center backend is wired in.
    activeCalls: [] as any[],
    stats: { activeCalls: 0, callsInQueue: 0, agentsOnline: 0 },
  };
}
